/**
 * Scratch & Win — the switch, the prize table, and who got what.
 *
 * Config lives in `app_config/scratchCard` (direct admin write, rules-allowed).
 * Results are `scratch_cards/{round}_{uid}` docs the sync worker creates when a
 * dealer opens their card — the prize is decided there, never in a client. This
 * page reads them live, so a dealer scratching shows up within a second.
 *
 * Rounds: every dealer gets ONE card per round. "Start a new round" changes the
 * round key so everyone gets a fresh card; earlier rounds stay in the table
 * under their own key (pick them in the Round filter).
 *
 * TESTING: "Reset card" deletes one dealer's card so their next visit draws a
 * fresh one (the app notices the card vanish and re-draws on its own), and
 * "Delete all cards in this round" clears a round. The prize-distribution
 * table shows actual draws against the configured shares, so repeated resets
 * make the odds visible.
 */
import { useEffect, useMemo, useState } from 'react';
import { deleteDoc, deleteField, doc, setDoc, updateDoc, writeBatch } from 'firebase/firestore';
import {
  COLLECTIONS,
  DEFAULT_SCRATCH_PRIZES,
  scratchClaimRef,
  type ScratchCard,
  type ScratchCardConfig,
  type ScratchPrize,
  type ScratchPrizeKind,
  type User,
} from '@mitang/shared';
import { db } from '../lib/firebase';
import { useAuth } from '../lib/auth';
import { useToast } from '../lib/toast';
import { errorMessage } from '../lib/errors';
import { scratchCardConfigDoc } from '../lib/collections';
import { useScratchCardConfig, useScratchCards, useUsers } from '../hooks/useFirestore';
import { toCsv, downloadCsv } from '../lib/csv';
import { formatDate, formatDateTime, timeAgo } from '../lib/format';
import { Badge, Button, Card, SectionHeader } from '../components/ui';
import { StatCard } from '../components/StatCard';
import { EmptyState, ErrorState, LoadingState } from '../components/States';
import { ActivityTimeline } from '../components/ActivityTimeline';

/* ------------------------------- helpers --------------------------------- */

type CardStatus = 'unscratched' | 'blank' | 'pending' | 'redeemed' | 'expired';

function statusOf(card: ScratchCard, now: number): CardStatus {
  if (!card.revealedAt) return 'unscratched';
  if (!card.won) return 'blank';
  if (card.redeemedAt) return 'redeemed';
  if (card.expiresAt && card.expiresAt < now) return 'expired';
  return 'pending';
}

const STATUS_META: Record<CardStatus, { label: string; tone: 'neutral' | 'brand' | 'danger' | 'success' | 'warn' }> = {
  unscratched: { label: 'Not scratched yet', tone: 'neutral' },
  blank: { label: 'No prize', tone: 'neutral' },
  pending: { label: 'To be given', tone: 'warn' },
  redeemed: { label: 'Given', tone: 'success' },
  expired: { label: 'Expired', tone: 'danger' },
};

const KIND_LABEL: Record<ScratchPrizeKind, string> = {
  cashback: 'Cashback',
  product: 'Item',
  none: 'No prize',
};

/** `r-20260925-k3f1` — sortable, readable, and safe inside a doc id. */
function newRoundId(): string {
  const d = new Date();
  const ymd = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
  const rand = Math.random().toString(36).slice(2, 6);
  return `r-${ymd}-${rand}`;
}

function slugId(title: string, taken: Set<string>): string {
  const base =
    title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 32) || 'prize';
  let id = base;
  let n = 2;
  while (taken.has(id)) id = `${base}-${n++}`;
  return id;
}

/** Editor row: a prize plus a stable React key (new rows have no id yet). */
interface PrizeRow extends Omit<ScratchPrize, 'id'> {
  id: string | null;
  key: string;
}

let rowSeq = 0;
function toRows(prizes: ScratchPrize[]): PrizeRow[] {
  return prizes.map((p) => ({ ...p, key: `k${rowSeq++}` }));
}

/* --------------------------------- page ---------------------------------- */

export function ScratchCardsPage(): JSX.Element {
  const toast = useToast();
  const { user: admin } = useAuth();
  const config = useScratchCardConfig();
  const cards = useScratchCards();
  const users = useUsers();

  const [busy, setBusy] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(t);
  }, []);

  const cfg = config.data;
  const currentRound = cfg?.round ?? '';

  /* ---- config writes ---- */
  const writeConfig = async (patch: Partial<ScratchCardConfig>, label: string): Promise<void> => {
    setBusy(label);
    try {
      await setDoc(
        scratchCardConfigDoc,
        {
          ...patch,
          updatedAt: Date.now(),
          ...(admin?.uid ? { updatedBy: admin.uid } : {}),
        } as ScratchCardConfig,
        { merge: true },
      );
    } catch (e) {
      toast.error(`Save failed: ${errorMessage(e)}`);
      throw e;
    } finally {
      setBusy(null);
    }
  };

  const setUp = async (): Promise<void> => {
    await writeConfig(
      {
        enabled: false,
        round: newRoundId(),
        title: 'Scratch & Win',
        subtitle: 'Every dealer gets one card this round. Scratch to see yours!',
        prizes: [...DEFAULT_SCRATCH_PRIZES],
      },
      'setup',
    );
    toast.success('Default prizes loaded. Turn it ON when you are ready.');
  };

  const toggle = async (): Promise<void> => {
    if (!cfg) return;
    if (!cfg.enabled && (cfg.prizes?.length ?? 0) === 0) {
      toast.error('Add at least one prize before turning it on.');
      return;
    }
    await writeConfig({ enabled: !cfg.enabled }, 'toggle');
    toast.success(cfg.enabled ? 'Scratch & win is OFF — the card disappears from the app.' : 'Scratch & win is ON — dealers see the card now.');
  };

  const startNewRound = async (): Promise<void> => {
    if (!cfg) return;
    if (
      !window.confirm(
        'Start a new round? Every dealer gets a fresh card (including those who already scratched). Results from this round stay in the table under its own round key.',
      )
    )
      return;
    const round = newRoundId();
    await writeConfig({ round }, 'round');
    setRoundFilter(round);
    toast.success(`New round ${round} started.`);
  };

  /* ---- prize editor ---- */
  const [rows, setRows] = useState<PrizeRow[]>([]);
  const [dirty, setDirty] = useState(false);
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  useEffect(() => {
    if (dirty || !cfg) return;
    setRows(toRows(cfg.prizes ?? []));
    setTitle(cfg.title ?? '');
    setSubtitle(cfg.subtitle ?? '');
  }, [cfg, dirty]);

  const edit = (key: string, patch: Partial<PrizeRow>) => {
    setDirty(true);
    setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  };
  const addRow = () => {
    setDirty(true);
    setRows((rs) => [...rs, { id: null, key: `k${rowSeq++}`, title: '', kind: 'cashback', weight: 0 }]);
  };
  const removeRow = (key: string) => {
    setDirty(true);
    setRows((rs) => rs.filter((r) => r.key !== key));
  };
  const totalWeight = rows.reduce((s, r) => s + (Number.isFinite(r.weight) ? r.weight : 0), 0);

  const savePrizes = async (): Promise<void> => {
    const taken = new Set(rows.map((r) => r.id).filter((x): x is string => !!x));
    const prizes: ScratchPrize[] = [];
    for (const r of rows) {
      const t = r.title.trim();
      if (!t) return toast.error('Every prize needs a title.');
      if (!(r.weight >= 0)) return toast.error(`"${t}": the share must be a number (0 or more).`);
      const id = r.id ?? slugId(t, taken);
      taken.add(id);
      const validityDays = r.kind !== 'none' && r.validityDays && r.validityDays > 0 ? Math.round(r.validityDays) : undefined;
      const description = r.description?.trim();
      prizes.push({
        id,
        title: t,
        kind: r.kind,
        weight: r.weight,
        ...(description ? { description } : {}),
        ...(validityDays ? { validityDays } : {}),
      });
    }
    if (prizes.every((p) => p.weight <= 0)) return toast.error('At least one prize needs a share above 0%.');
    await writeConfig({ prizes, title: title.trim(), subtitle: subtitle.trim() }, 'prizes');
    setDirty(false);
    toast.success('Prizes saved. New cards use these odds from now on.');
  };

  const discard = () => {
    setDirty(false);
    if (cfg) {
      setRows(toRows(cfg.prizes ?? []));
      setTitle(cfg.title ?? '');
      setSubtitle(cfg.subtitle ?? '');
    }
  };

  /* ---- results ---- */
  const [roundFilter, setRoundFilter] = useState<string>('');
  const [prizeFilter, setPrizeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<CardStatus | 'all'>('all');
  const [search, setSearch] = useState('');
  const [drawerUser, setDrawerUser] = useState<User | null>(null);
  const effectiveRound = roundFilter || currentRound;

  const rounds = useMemo(() => {
    const set = new Set<string>();
    if (currentRound) set.add(currentRound);
    for (const c of cards.data) set.add(c.round);
    return [...set];
  }, [cards.data, currentRound]);

  const prizeOptions = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of cards.data) if (c.round === effectiveRound || effectiveRound === 'all') m.set(c.prizeId, c.prizeTitle);
    for (const p of cfg?.prizes ?? []) if (!m.has(p.id)) m.set(p.id, p.title);
    return [...m.entries()];
  }, [cards.data, cfg?.prizes, effectiveRound]);

  const roundCards = useMemo(
    () => (effectiveRound === 'all' ? cards.data : cards.data.filter((c) => c.round === effectiveRound)),
    [cards.data, effectiveRound],
  );

  const summary = useMemo(() => {
    const s = { drawn: roundCards.length, scratched: 0, won: 0, pending: 0, redeemed: 0 };
    for (const c of roundCards) {
      const st = statusOf(c, now);
      if (st !== 'unscratched') s.scratched++;
      if (c.won && st !== 'unscratched') s.won++;
      if (st === 'pending') s.pending++;
      if (st === 'redeemed') s.redeemed++;
    }
    return s;
  }, [roundCards, now]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return roundCards.filter((c) => {
      if (prizeFilter !== 'all' && c.prizeId !== prizeFilter) return false;
      if (statusFilter !== 'all' && statusOf(c, now) !== statusFilter) return false;
      if (!q) return true;
      return [c.email, c.name, c.companyName, c.city, c.mobile, c.prizeTitle, scratchClaimRef(c.id)]
        .some((v) => (v ?? '').toLowerCase().includes(q));
    });
  }, [roundCards, prizeFilter, statusFilter, search, now]);

  const usersById = useMemo(() => new Map(users.data.map((u) => [u.id, u])), [users.data]);

  const setRedeemed = async (card: ScratchCard, redeemed: boolean): Promise<void> => {
    setBusy(card.id);
    try {
      const ref = doc(db, COLLECTIONS.scratchCards, card.id);
      if (redeemed) {
        const note = window.prompt(`Mark "${card.prizeTitle}" as given to ${card.name || card.email}?\n\nOptional note (e.g. invoice number):`, card.note ?? '');
        if (note === null) return;
        await updateDoc(ref, {
          redeemedAt: Date.now(),
          ...(admin?.uid ? { redeemedBy: admin.uid } : {}),
          ...(note.trim() ? { note: note.trim() } : {}),
        });
        toast.success(`Marked as given: ${card.prizeTitle} → ${card.name || card.email}.`);
      } else {
        await updateDoc(ref, { redeemedAt: deleteField(), redeemedBy: deleteField() });
        toast.info('Undone — back to "to be given".');
      }
    } catch (e) {
      toast.error(`Update failed: ${errorMessage(e)}`);
    } finally {
      setBusy(null);
    }
  };

  /**
   * TESTING AID: delete a dealer's card so they can draw again. The app is
   * listening to the doc, sees it disappear and asks the worker for a new one
   * the next time the card screen is open. Nothing else is touched.
   */
  const resetCard = async (card: ScratchCard): Promise<void> => {
    if (
      !window.confirm(
        `Reset the card of ${card.name || card.email || card.uid}?\n\nTheir "${card.prizeTitle}" card is deleted and they get a FRESH draw next time they open Scratch & Win. Use this for testing only — a real winner would lose their prize.`,
      )
    )
      return;
    setBusy(card.id);
    try {
      await deleteDoc(doc(db, COLLECTIONS.scratchCards, card.id));
      toast.success(`Card reset — ${card.name || card.email || 'the dealer'} can scratch again.`);
    } catch (e) {
      toast.error(`Reset failed: ${errorMessage(e)}`);
    } finally {
      setBusy(null);
    }
  };

  /** TESTING AID: wipe every card of the selected round (batched, 400 per commit). */
  const resetRound = async (): Promise<void> => {
    const targets = roundCards;
    if (targets.length === 0) return;
    if (
      !window.confirm(
        `Delete ALL ${targets.length} card${targets.length === 1 ? '' : 's'} ${effectiveRound === 'all' ? 'in every round' : `in round ${effectiveRound}`}?\n\nEvery affected dealer gets a fresh draw next time. This cannot be undone — for testing only.`,
      )
    )
      return;
    setBusy('reset-round');
    try {
      for (let i = 0; i < targets.length; i += 400) {
        const batch = writeBatch(db);
        for (const c of targets.slice(i, i + 400)) batch.delete(doc(db, COLLECTIONS.scratchCards, c.id));
        await batch.commit();
      }
      toast.success(`Deleted ${targets.length} card${targets.length === 1 ? '' : 's'}.`);
    } catch (e) {
      toast.error(`Reset failed: ${errorMessage(e)}`);
    } finally {
      setBusy(null);
    }
  };

  /**
   * Actual draws per prize vs the configured share — the odds, made visible.
   * Prizes no longer in the table (renamed/removed) still show, from the cards.
   */
  const distribution = useMemo(() => {
    const counts = new Map<string, { title: string; count: number }>();
    for (const c of roundCards) {
      const e = counts.get(c.prizeId) ?? { title: c.prizeTitle, count: 0 };
      e.count++;
      counts.set(c.prizeId, e);
    }
    const prizes = cfg?.prizes ?? [];
    const totalWeight = prizes.reduce((sum, p) => sum + Math.max(0, p.weight), 0);
    const rows = prizes.map((p) => ({
      id: p.id,
      title: p.title,
      configured: totalWeight > 0 ? (Math.max(0, p.weight) / totalWeight) * 100 : 0,
      count: counts.get(p.id)?.count ?? 0,
    }));
    for (const [id, e] of counts) {
      if (!prizes.some((p) => p.id === id)) rows.push({ id, title: `${e.title} (no longer in the table)`, configured: 0, count: e.count });
    }
    const total = roundCards.length;
    return { rows: rows.map((r) => ({ ...r, actual: total > 0 ? (r.count / total) * 100 : 0 })), total };
  }, [roundCards, cfg?.prizes]);

  const exportCsv = () => {
    const data = filtered.map((c) => ({
      round: c.round,
      email: c.email ?? '',
      name: c.name ?? '',
      company: c.companyName ?? '',
      city: c.city ?? '',
      mobile: c.mobile ?? '',
      prize: c.prizeTitle,
      ref: c.won ? scratchClaimRef(c.id) : '',
      type: KIND_LABEL[c.prizeKind],
      won: c.won ? 'yes' : 'no',
      status: STATUS_META[statusOf(c, now)].label,
      drawnAt: formatDateTime(c.drawnAt),
      scratchedAt: c.revealedAt ? formatDateTime(c.revealedAt) : '',
      validTill: c.expiresAt ? formatDate(c.expiresAt) : '',
      redeemedAt: c.redeemedAt ? formatDateTime(c.redeemedAt) : '',
      note: c.note ?? '',
    }));
    downloadCsv(
      `scratch-cards-${effectiveRound}-${new Date().toISOString().slice(0, 10)}.csv`,
      toCsv(data, [
        { key: 'round', header: 'Round' },
        { key: 'email', header: 'Email' },
        { key: 'name', header: 'Name' },
        { key: 'company', header: 'Company' },
        { key: 'city', header: 'City' },
        { key: 'mobile', header: 'Mobile' },
        { key: 'prize', header: 'Prize' },
        { key: 'ref', header: 'Claim ref' },
        { key: 'type', header: 'Type' },
        { key: 'won', header: 'Won' },
        { key: 'status', header: 'Status' },
        { key: 'drawnAt', header: 'Card opened' },
        { key: 'scratchedAt', header: 'Scratched' },
        { key: 'validTill', header: 'Valid till' },
        { key: 'redeemedAt', header: 'Given on' },
        { key: 'note', header: 'Note' },
      ]),
    );
  };

  /* --------------------------------- render -------------------------------- */

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Scratch & Win"
        subtitle="Turn the card on or off, set the prizes and their odds, and see which dealer got what."
        actions={
          <>
            {cfg ? (
              <Badge tone={cfg.enabled ? 'success' : 'neutral'}>{cfg.enabled ? 'ON' : 'OFF'}</Badge>
            ) : null}
            <Button variant="secondary" size="sm" onClick={exportCsv} disabled={filtered.length === 0}>
              Export CSV
            </Button>
          </>
        }
      />

      {config.error ? <ErrorState message={config.error} /> : null}

      {config.loading ? (
        <LoadingState label="Loading…" />
      ) : !cfg ? (
        <Card>
          <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-5">
            <div>
              <p className="text-sm font-semibold text-slate-900">Scratch & win is not set up yet</p>
              <p className="text-xs text-slate-500">
                Load the default prize table (₹250 / ₹500 cashback, HP mouse, HP wireless combo, better luck next time). It stays OFF until you turn it on.
              </p>
            </div>
            <Button onClick={() => void setUp()} loading={busy === 'setup'}>
              Use default prizes
            </Button>
          </div>
        </Card>
      ) : (
        <Card>
          <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
            <div>
              <p className="text-sm font-semibold text-slate-900">
                Scratch & win: {cfg.enabled ? 'ON' : 'OFF'}
              </p>
              <p className="text-xs text-slate-500">
                {cfg.enabled
                  ? 'Dealers see a "Scratch & Win" card on the home screen — one card each for this round.'
                  : 'Nothing is shown in the app. Prizes and results are kept.'}
              </p>
              <p className="mt-1 text-xs text-slate-500">
                Current round: <span className="font-mono font-medium text-slate-700">{cfg.round}</span>
                {' · '}
                <button
                  type="button"
                  className="font-medium text-brand-600 hover:underline disabled:opacity-50"
                  onClick={() => void startNewRound()}
                  disabled={busy !== null}
                >
                  Start a new round
                </button>
                <span className="text-slate-400"> (everyone gets a fresh card)</span>
              </p>
            </div>
            <Button
              variant={cfg.enabled ? 'secondary' : 'primary'}
              onClick={() => void toggle()}
              loading={busy === 'toggle'}
              disabled={busy !== null}
            >
              {cfg.enabled ? 'Turn OFF' : 'Turn ON'}
            </Button>
          </div>
        </Card>
      )}

      {cfg ? (
        <Card>
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-5 py-3">
            <div>
              <h2 className="text-sm font-semibold text-slate-900">Prizes &amp; odds</h2>
              <p className="text-xs text-slate-500">
                Share = out of every 100 cards. Changes apply to cards opened from now on; cards already drawn keep their prize.
              </p>
            </div>
            <span
              className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                Math.abs(totalWeight - 100) < 0.001 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
              }`}
              title="The worker scales the shares to whatever they add up to, so 90 still works — but 100 is easiest to reason about."
            >
              Total {Number.isInteger(totalWeight) ? totalWeight : totalWeight.toFixed(1)}%
            </span>
          </div>

          <div className="grid gap-3 px-5 py-4 sm:grid-cols-2">
            <label className="text-xs font-medium text-slate-500">
              Card headline
              <input
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm font-normal text-slate-800 outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
                value={title}
                maxLength={40}
                placeholder="Scratch & Win"
                onChange={(e) => {
                  setDirty(true);
                  setTitle(e.target.value);
                }}
              />
            </label>
            <label className="text-xs font-medium text-slate-500">
              Line under it
              <input
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm font-normal text-slate-800 outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
                value={subtitle}
                maxLength={90}
                placeholder="Every dealer gets one card this round. Scratch to see yours!"
                onChange={(e) => {
                  setDirty(true);
                  setSubtitle(e.target.value);
                }}
              />
            </label>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px] text-left text-sm">
              <thead className="border-y border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-5 py-2.5 font-medium">Prize</th>
                  <th className="px-3 py-2.5 font-medium">Details shown on the card</th>
                  <th className="px-3 py-2.5 font-medium">Type</th>
                  <th className="px-3 py-2.5 font-medium">Share %</th>
                  <th className="px-3 py-2.5 font-medium">Valid for (days)</th>
                  <th className="px-3 py-2.5" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r) => (
                  <tr key={r.key}>
                    <td className="px-5 py-2">
                      <input
                        className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
                        value={r.title}
                        maxLength={60}
                        placeholder="e.g. ₹250 cashback"
                        onChange={(e) => edit(r.key, { title: e.target.value })}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
                        value={r.description ?? ''}
                        maxLength={140}
                        placeholder={r.kind === 'none' ? '(optional)' : 'e.g. On your next order of an HP laptop, desktop or AIO'}
                        onChange={(e) => edit(r.key, { description: e.target.value })}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <select
                        className="rounded-md border border-slate-300 px-2 py-1.5 text-sm outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
                        value={r.kind}
                        onChange={(e) => edit(r.key, { kind: e.target.value as ScratchPrizeKind })}
                      >
                        <option value="cashback">Cashback</option>
                        <option value="product">Item</option>
                        <option value="none">No prize</option>
                      </select>
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="number"
                        min={0}
                        max={100}
                        step={1}
                        className="w-20 rounded-md border border-slate-300 px-2.5 py-1.5 text-sm tabular-nums outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
                        value={Number.isFinite(r.weight) ? r.weight : ''}
                        onChange={(e) => edit(r.key, { weight: e.target.value === '' ? NaN : Number(e.target.value) })}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="number"
                        min={0}
                        step={1}
                        disabled={r.kind === 'none'}
                        className="w-24 rounded-md border border-slate-300 px-2.5 py-1.5 text-sm tabular-nums outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500 disabled:bg-slate-50 disabled:text-slate-400"
                        value={r.kind === 'none' ? '' : (r.validityDays ?? '')}
                        placeholder="no expiry"
                        onChange={(e) => edit(r.key, { validityDays: e.target.value === '' ? undefined : Number(e.target.value) })}
                      />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        className="rounded px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50"
                        onClick={() => removeRow(r.key)}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-5 py-6 text-center text-sm text-slate-500">
                      No prizes. Add one, or load the defaults.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 px-5 py-3">
            <Button variant="secondary" size="sm" onClick={addRow}>
              + Add prize
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setDirty(true);
                setRows(toRows([...DEFAULT_SCRATCH_PRIZES]));
              }}
            >
              Reset to defaults
            </Button>
            <span className="flex-1" />
            {dirty ? (
              <>
                <Button variant="ghost" size="sm" onClick={discard} disabled={busy !== null}>
                  Discard
                </Button>
                <Button size="sm" onClick={() => void savePrizes()} loading={busy === 'prizes'} disabled={busy !== null}>
                  Save prizes
                </Button>
              </>
            ) : (
              <span className="text-xs text-slate-400">
                Saved {cfg.updatedAt ? timeAgo(cfg.updatedAt) : ''}
              </span>
            )}
          </div>
        </Card>
      ) : null}

      {/* ------------------------------ results ------------------------------ */}
      {cards.error ? (
        <ErrorState message={cards.error} />
      ) : (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <StatCard label="Cards opened" value={summary.drawn} loading={cards.loading} sublabel={effectiveRound === 'all' ? 'all rounds' : `round ${effectiveRound}`} />
          <StatCard label="Scratched" value={summary.scratched} tone="brand" loading={cards.loading} sublabel={`${summary.drawn - summary.scratched} opened but not scratched`} />
          <StatCard label="Won a prize" value={summary.won} tone="emerald" loading={cards.loading} sublabel={`${summary.scratched - summary.won} got "better luck next time"`} />
          <StatCard label="To be given" value={summary.pending} tone={summary.pending > 0 ? 'amber' : 'neutral'} loading={cards.loading} sublabel={`${summary.redeemed} already given`} />
        </div>
      )}

      {!cards.error ? (
        <Card>
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-5 py-3">
            <div>
              <h2 className="text-sm font-semibold text-slate-900">Prize distribution</h2>
              <p className="text-xs text-slate-500">
                What was actually drawn {effectiveRound === 'all' ? 'across all rounds' : `in round ${effectiveRound}`} vs the configured share.
                Reset a card (or the whole round) to draw again and watch the odds settle.
              </p>
            </div>
            <Button
              variant="danger"
              size="sm"
              onClick={() => void resetRound()}
              loading={busy === 'reset-round'}
              disabled={busy !== null || roundCards.length === 0}
              title="Testing aid: every dealer in this round gets a fresh draw"
            >
              Delete all cards {effectiveRound === 'all' ? '' : 'in this round'}
            </Button>
          </div>
          {cards.loading ? (
            <div className="p-4">
              <LoadingState label="Loading…" />
            </div>
          ) : distribution.rows.length === 0 ? (
            <div className="p-4">
              <EmptyState title="No prizes configured" />
            </div>
          ) : (
            <table className="w-full text-left text-sm">
              <thead className="border-b border-slate-100 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-5 py-2 font-medium">Prize</th>
                  <th className="px-3 py-2 text-right font-medium">Configured</th>
                  <th className="px-3 py-2 text-right font-medium">Drawn</th>
                  <th className="px-3 py-2 text-right font-medium">Actual</th>
                  <th className="w-1/3 px-5 py-2 font-medium">Actual vs configured</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {distribution.rows.map((r) => (
                  <tr key={r.id}>
                    <td className="px-5 py-2 text-slate-800">{r.title}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-500">{r.configured.toFixed(0)}%</td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-800">{r.count}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-medium text-slate-800">
                      {distribution.total > 0 ? `${r.actual.toFixed(0)}%` : '—'}
                    </td>
                    <td className="px-5 py-2">
                      <div className="relative h-2.5 w-full rounded-full bg-slate-100">
                        <div className="absolute inset-y-0 left-0 rounded-full bg-brand-500" style={{ width: `${Math.min(100, r.actual)}%` }} />
                        <div
                          className="absolute -top-0.5 h-3.5 w-0.5 bg-slate-500"
                          style={{ left: `${Math.min(100, r.configured)}%` }}
                          title={`configured ${r.configured.toFixed(0)}%`}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-slate-100 text-xs text-slate-500">
                  <td className="px-5 py-2" colSpan={2}>
                    {distribution.total} card{distribution.total === 1 ? '' : 's'} drawn · the tick marks the configured share; the bar is what was drawn
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{distribution.total}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{distribution.total > 0 ? '100%' : '—'}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          )}
        </Card>
      ) : null}

      <Card className="p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search dealer, company, city, mobile, prize, claim ref…"
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500 sm:max-w-xs"
          />
          <select
            value={effectiveRound}
            onChange={(e) => setRoundFilter(e.target.value)}
            className="rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
          >
            {rounds.map((r) => (
              <option key={r} value={r}>
                {r === currentRound ? `Current round (${r})` : `Round ${r}`}
              </option>
            ))}
            <option value="all">All rounds</option>
          </select>
          <select
            value={prizeFilter}
            onChange={(e) => setPrizeFilter(e.target.value)}
            className="rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
          >
            <option value="all">All prizes</option>
            {prizeOptions.map(([id, label]) => (
              <option key={id} value={id}>
                {label}
              </option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as CardStatus | 'all')}
            className="rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
          >
            <option value="all">Any status</option>
            <option value="pending">To be given</option>
            <option value="redeemed">Given</option>
            <option value="unscratched">Not scratched yet</option>
            <option value="blank">No prize</option>
            <option value="expired">Expired</option>
          </select>
          <span className="text-xs text-slate-500 sm:ml-auto">
            Showing {filtered.length} of {roundCards.length}
          </span>
        </div>
      </Card>

      {cards.loading ? (
        <LoadingState label="Loading cards…" />
      ) : roundCards.length === 0 ? (
        <EmptyState
          title={cfg?.enabled ? 'No cards opened yet this round' : 'No cards yet'}
          hint={cfg?.enabled ? 'A row appears the moment a dealer opens their card in the app.' : 'Turn scratch & win ON and dealers will start opening cards.'}
          icon="🎟️"
        />
      ) : filtered.length === 0 ? (
        <EmptyState title="No matches" hint="Try a different search or filter." />
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] text-left text-sm">
              <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3 font-medium">Dealer</th>
                  <th className="px-4 py-3 font-medium">Company · City</th>
                  <th className="px-4 py-3 font-medium">Prize</th>
                  <th className="px-4 py-3 font-medium">Scratched</th>
                  <th className="px-4 py-3 font-medium">Valid till</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((c) => {
                  const st = statusOf(c, now);
                  const meta = STATUS_META[st];
                  const u = usersById.get(c.uid) ?? null;
                  return (
                    <tr key={c.id} className="transition-colors hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          className="text-left disabled:cursor-default"
                          disabled={!u}
                          onClick={() => u && setDrawerUser(u)}
                          title={u ? 'Open activity timeline' : undefined}
                        >
                          <div className="font-medium text-slate-800">{c.email || c.uid}</div>
                          <div className="text-xs text-slate-400">{c.name || '—'}{c.mobile ? ` · ${c.mobile}` : ''}</div>
                        </button>
                      </td>
                      <td className="px-4 py-3 text-slate-600">
                        {c.companyName || '—'}
                        {c.city ? <span className="text-slate-400"> · {c.city}</span> : null}
                      </td>
                      <td className="px-4 py-3">
                        <div className={`font-medium ${c.won ? 'text-slate-800' : 'text-slate-400'}`}>{c.prizeTitle}</div>
                        <div className="text-xs text-slate-400">
                          {c.won ? <span className="font-mono text-slate-500">{scratchClaimRef(c.id)}</span> : KIND_LABEL[c.prizeKind]}
                          {c.won ? ` · ${KIND_LABEL[c.prizeKind]}` : ''}
                          {effectiveRound === 'all' ? ` · ${c.round}` : ''}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-600" title={c.revealedAt ? formatDateTime(c.revealedAt) : `Opened ${formatDateTime(c.drawnAt)}`}>
                        {c.revealedAt ? timeAgo(c.revealedAt) : <span className="text-slate-400">not yet · opened {timeAgo(c.drawnAt)}</span>}
                      </td>
                      <td className="px-4 py-3 text-slate-600">{c.won && c.expiresAt ? formatDate(c.expiresAt) : '—'}</td>
                      <td className="px-4 py-3">
                        <Badge tone={meta.tone}>{meta.label}</Badge>
                        {c.note ? <div className="mt-0.5 max-w-[220px] truncate text-xs text-slate-400" title={c.note}>{c.note}</div> : null}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex items-center gap-1.5">
                          {st === 'pending' || st === 'expired' ? (
                            <Button size="sm" variant="success" onClick={() => void setRedeemed(c, true)} loading={busy === c.id} disabled={busy !== null}>
                              Mark given
                            </Button>
                          ) : st === 'redeemed' ? (
                            <Button size="sm" variant="ghost" onClick={() => void setRedeemed(c, false)} loading={busy === c.id} disabled={busy !== null}>
                              Undo
                            </Button>
                          ) : null}
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-600 hover:bg-red-50"
                            onClick={() => void resetCard(c)}
                            disabled={busy !== null}
                            title="Testing aid: delete this card so the dealer draws again"
                          >
                            Reset card
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <ActivityTimeline user={drawerUser} open={drawerUser !== null} onClose={() => setDrawerUser(null)} />
    </div>
  );
}
